// Route — the client's URL contract as ONE closed sum (F48 URL navigation).
//
// The URL is the client's PUBLIC API, sibling to (not overlapping) the server's
// data API: the server answers "what does the workspace contain", the URL names
// "what is being looked at, and how." A `Route` is a projection of the frame's
// ONE focused view into a shareable string, and — the other way — a URL is
// PARSED into a `Route` (parse-don't-validate) that is INTERPRETED as existing
// `FrameTransition`s. The URL is never a second source of truth; the frame
// decider stays the single writer and the URL mirrors it.
// Full design: .claude/analysis/url-navigation-design-2026-09-22.md.
//
// Full standard-navigable web app (F48 v2): EVERY view has its OWN distinct
// path — no two destinations share one URL. In particular the cold-open NEW
// CHAT (a nascent, id-less compose) and the JOURNAL overview no longer collide
// at `/`: `/` is the new chat, `/journal` is the overview. An unrecognized path
// is a real `notFound`, NOT a silent fallback to a real view (v2 behavior
// change from v1's degrade-to-home).
//
// Naming seam the design flags: the resource-kind vocab disagrees
// (`ResourceRef["type"]` says "export", `SearchResultKind` says "package"); the
// URL is human-facing, so it uses the human word `/package` (package ⇄ the
// `export` ref type is mapped at the bridge, never a cast).

import type { ResourceId } from "../../components/Navigation/types.js";
import type { SearchFacet } from "../../components/Search/searchModel.js";
import type { SettingsSectionKind } from "../../components/Settings/types.js";

// The root — a fresh NEW CHAT (nascent compose). Matches chat-primary + the
// cold-open resting state. Id-less by design: a nascent conversation's id is
// client-minted and ephemeral, so it must never bake into the one bookmarkable
// root URL.
export type RouteNewChat = { readonly type: "chat" };

// The workspace overview / Journal (the `open-home` destination — WorkspaceHome).
type RouteJournal = { readonly type: "journal" };

// The Studio: on a project (`/projects/:id`), or bare (`/studio`, the one
// the author is in).
type RouteStudio = { readonly type: "studio"; readonly projectId?: string };

// The old Studio (the canvas), during the transition to the three columns:
// only at a hand-typed `/classic/studio` or `/classic/projects/:id`, linked
// from nowhere; removed when the owner retires it.
type RouteClassicStudio = {
    readonly type: "classicStudio";
    readonly projectId?: string;
};

// The author's projects (#161): the list; a project opens in the Studio.
type RouteProjects = { readonly type: "projects" };

// The Knowledge bank (#60): the workspace's sources. `selected` is the source
// gone into (`/knowledge/{id}`, its viewer); `/source/:id` parses to this.
type RouteKnowledge = {
    readonly type: "knowledge";
    readonly selected?: ResourceId;
};

// The library browser (Bibliotek — the `open-search` Search + Recent surface).
// `query`/`filter` are optional refinements over one destination, so they ride
// the query string. `query` "" is Recent; `facet` undefined is unfiltered.
type RouteLibrary = {
    readonly type: "library";
    readonly query: string;
    readonly facet?: SearchFacet;
    // The selected row (`/library/{id}`) — survives reload, so it is in the URL.
    // `/material/:id` parses to this (facet "material") — a material lives in
    // the library detail.
    readonly selected?: ResourceId;
};

// A SAVED (durable) conversation, addressed by its durable id. The nascent chat
// is the only id-less conversation and lives at `/` (RouteNewChat).
type RouteConversation = {
    readonly type: "conversation";
    readonly conversationId: string;
};

type RoutePackage = {
    readonly type: "package";
    readonly id: ResourceId;
};

// A material's full-quality report — its own standalone CENTRE tab
// (`open-quality-report` mounts a QualityTab, not a dock panel), so its own
// path. (The dock's `summon-quality` panel is a modifier, not a destination —
// deliberately NOT a route.)
type RouteQuality = {
    readonly type: "quality";
    readonly materialId: ResourceId;
};

// A Settings section — the identity of WHICH page, so a path segment.
type RouteSettings = {
    readonly type: "settings";
    readonly section: SettingsSectionKind;
};

// An unrecognized path — a real 404. Carries the original pathname so the
// address bar keeps showing it (a genuine 404 shows the URL you typed) and so
// `routeToUrl` can reproduce it for the idempotence comparison.
type RouteNotFound = {
    readonly type: "notFound";
    readonly path: string;
};

export type Route =
    | RouteNewChat
    | RouteJournal
    | RouteStudio
    | RouteClassicStudio
    | RouteProjects
    | RouteKnowledge
    | RouteLibrary
    | RouteConversation
    | RoutePackage
    | RouteQuality
    | RouteSettings
    | RouteNotFound;

export const routeNewChat: RouteNewChat = { type: "chat" };
